import React from 'react';
import { Users, UserPlus, Trash2, Save, ExternalLink, RefreshCw } from 'lucide-react';

const AdminUsersTab = ({ 
  adminUsers,
  isLoadingUsers,
  showNewUserForm,
  setShowNewUserForm,
  newUser,
  setNewUser,
  availableRanks,
  createUser,
  updateUserRole,
  deleteUser,
  currentUserId
}) => {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-marine-cyan text-2xl uppercase tracking-wider flex items-center gap-3">
          <Users className="w-7 h-7" />
          Gestion des Utilisateurs
        </h2>
        <button
          onClick={() => setShowNewUserForm(!showNewUserForm)}
          className="px-6 py-3 bg-marine-green/20 border-2 border-marine-green text-marine-green hover:bg-marine-green hover:text-black font-mono text-sm uppercase tracking-wider flex items-center gap-2"
        >
          <UserPlus className="w-5 h-5" />
          Nouvel Utilisateur
        </button>
      </div>

      {/* New User Form */}
      {showNewUserForm && (
        <div className="bg-black border-2 border-marine-green/50 p-6">
          <h3 className="font-heading text-marine-green text-lg uppercase tracking-wider mb-4 flex items-center gap-2">
            <UserPlus className="w-5 h-5" />
            Créer un Utilisateur
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block font-mono text-white/90 text-sm uppercase tracking-wider mb-2">Email</label>
              <input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                className="w-full bg-black border border-marine-slate/50 text-white font-mono text-base p-3 focus:border-marine-green"
                placeholder="utilisateur@email.com"
              />
            </div>
            <div>
              <label className="block font-mono text-white/90 text-sm uppercase tracking-wider mb-2">Mot de passe</label>
              <input
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                className="w-full bg-black border border-marine-slate/50 text-white font-mono text-base p-3 focus:border-marine-green"
                placeholder="••••••••"
              />
            </div>
            <div>
              <label className="block font-mono text-white/90 text-sm uppercase tracking-wider mb-2">Nom complet</label>
              <input
                type="text"
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                className="w-full bg-black border border-marine-slate/50 text-white font-mono text-base p-3 focus:border-marine-green"
                placeholder="Prénom NOM"
              />
            </div>
            <div>
              <label className="block font-mono text-white/90 text-sm uppercase tracking-wider mb-2">Grade</label>
              <select
                value={newUser.rank}
                onChange={(e) => setNewUser({ ...newUser, rank: e.target.value })}
                className="w-full bg-black border border-marine-slate/50 text-white font-mono text-base p-3 focus:border-marine-green"
              >
                {availableRanks.map(r => (
                  <option key={r.key} value={r.key}>{r.title} ({r.code})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block font-mono text-white/90 text-sm uppercase tracking-wider mb-2">Rôle</label>
              <select
                value={newUser.role}
                onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                className="w-full bg-black border border-marine-slate/50 text-white font-mono text-base p-3 focus:border-marine-green"
              >
                <option value="admin">Admin (gestion utilisateurs)</option>
                <option value="analyst">Analyste (scan + rapports)</option>
                <option value="viewer">Lecteur (consultation)</option>
              </select>
            </div>
          </div>
          <div className="flex gap-4 mt-6">
            <button
              onClick={createUser}
              className="px-6 py-3 bg-marine-green/20 border-2 border-marine-green text-marine-green hover:bg-marine-green hover:text-black font-mono text-sm uppercase tracking-wider flex items-center gap-2"
            >
              <Save className="w-5 h-5" />
              Créer
            </button>
            <button
              onClick={() => setShowNewUserForm(false)}
              className="px-6 py-3 border border-marine-slate text-marine-slate hover:text-white font-mono text-sm uppercase tracking-wider"
            >
              Annuler
            </button>
          </div>
        </div>
      )}

      {/* Users List */}
      <div className="bg-black border-2 border-marine-slate/50">
        <div className="bg-marine-slate/30 px-4 py-3 border-b border-marine-slate/50">
          <span className="font-mono text-marine-cyan text-sm uppercase tracking-wider">
            {adminUsers.length} utilisateur(s) enregistré(s)
          </span>
        </div>
        {isLoadingUsers ? (
          <div className="p-8 text-center">
            <RefreshCw className="w-8 h-8 text-marine-cyan animate-spin mx-auto" />
          </div>
        ) : (
          <div className="divide-y divide-marine-slate/30">
            {adminUsers.map(u => (
              <div key={u.id} className="p-4 flex items-center justify-between hover:bg-marine-slate/10">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-white text-base font-bold">{u.name}</span>
                    <span className="px-2 py-1 bg-marine-cyan/20 text-marine-cyan text-xs font-mono uppercase">{u.rank_code}</span>
                    <span className={`px-2 py-1 text-xs font-mono uppercase ${
                      u.role === 'admin' ? 'bg-marine-amber/20 text-marine-amber' : 
                      u.role === 'analyst' ? 'bg-marine-green/20 text-marine-green' : 
                      'bg-marine-slate/20 text-marine-slate'
                    }`}>
                      {u.role || 'analyst'}
                    </span>
                  </div>
                  <p className="font-mono text-white/90 text-sm mt-1">{u.email}</p>
                  <p className="font-mono text-white/80 text-xs mt-1">{u.rank_title}</p>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={u.role || 'analyst'}
                    onChange={(e) => updateUserRole(u.id, e.target.value)}
                    className="bg-black border border-marine-slate/50 text-white font-mono text-sm p-2"
                  >
                    <option value="admin">Admin</option>
                    <option value="analyst">Analyste</option>
                    <option value="viewer">Lecteur</option>
                  </select>
                  <button
                    onClick={() => deleteUser(u.id)}
                    disabled={u.id === currentUserId}
                    className="p-2 text-marine-red hover:bg-marine-red/20 disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Supprimer"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* API Configuration Guide */}
      <div className="bg-black border-2 border-marine-cyan/50 p-6">
        <h3 className="font-heading text-marine-cyan text-lg uppercase tracking-wider mb-4 flex items-center gap-2">
          <ExternalLink className="w-5 h-5" />
          Connexion aux vraies APIs
        </h3>
        <p className="font-mono text-white/80 text-sm mb-4">
          Pour connecter l'outil aux sources de données réelles, procédez comme suit:
        </p>
        <div className="space-y-4 font-mono text-sm">
          <div className="p-3 bg-marine-slate/20 border-l-4 border-marine-green">
            <p className="text-marine-green font-bold mb-1">1. IEA (Agence Internationale de l'Énergie)</p>
            <p className="text-white/90">Créez un compte sur <span className="text-marine-cyan">iea.org/data-and-statistics</span></p>
            <p className="text-white/90">Demandez une clé API (gratuit pour usage non-commercial)</p>
          </div>
          <div className="p-3 bg-marine-slate/20 border-l-4 border-marine-amber">
            <p className="text-marine-amber font-bold mb-1">2. EIA (US Energy Information)</p>
            <p className="text-white/90">Inscription sur <span className="text-marine-cyan">eia.gov/opendata</span></p>
            <p className="text-white/90">Clé API gratuite et immédiate</p>
          </div>
          <div className="p-3 bg-marine-slate/20 border-l-4 border-marine-red">
            <p className="text-marine-red font-bold mb-1">3. Platts / S&P Global</p>
            <p className="text-white/90">API payante - Contacter <span className="text-marine-cyan">spglobal.com/commodityinsights</span></p>
            <p className="text-white/90">Nécessite un abonnement professionnel</p>
          </div>
        </div>
        <p className="font-mono text-white/90 text-xs mt-4">
          Une fois les clés obtenues, ajoutez-les dans Sources {">"} Configuration de la source concernée.
        </p>
      </div>
    </div>
  );
};

export default AdminUsersTab;
